/**
 * useUserShares — subscribe to all sharedLinks docs owned by the current user.
 *
 * Provides a fast lookup `isViewShared(view)` that returns true when:
 *   - a share doc exists for view.projectId
 *   - share.enabled === true
 *   - view.viewId is NOT in share.excludedViewIds
 */

import { useEffect, useState, useCallback } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';
import type { ProjectShare, View } from '@/types';

export function useUserShares() {
  const { user } = useAuthStore();
  const [sharesByProject, setSharesByProject] = useState<Record<string, ProjectShare>>({});

  useEffect(() => {
    if (!user?.uid) {
      setSharesByProject({});
      return;
    }
    const q = query(
      collection(db, 'sharedLinks'),
      where('userId', '==', user.uid),
    );
    const unsub = onSnapshot(q, (snap) => {
      const map: Record<string, ProjectShare> = {};
      snap.forEach((d) => {
        const data = d.data() as Omit<ProjectShare, 'shareId'>;
        const share = { shareId: d.id, ...data } as ProjectShare;
        map[share.projectId] = share;
      });
      setSharesByProject(map);
    });
    return () => unsub();
  }, [user?.uid]);

  const isViewShared = useCallback(
    (view: Pick<View, 'projectId' | 'viewId'>) => {
      const share = sharesByProject[view.projectId];
      if (!share || share.enabled !== true) return false;
      return !(share.excludedViewIds ?? []).includes(view.viewId);
    },
    [sharesByProject],
  );

  return { sharesByProject, isViewShared };
}
